import { useEffect, useMemo, useState } from 'react';

import { Card, Form, Button, Spinner } from 'react-bootstrap';
import { useApp } from 'common/AppContext';

const CUSTOM = '__custom__';

export const StepUrl = ({ initialValue, onNext }) => {

    const { api } = useApp();

    const [apps, setApps] = useState([]);
    const [appsLoading, setAppsLoading] = useState(true);
    const [appsError, setAppsError] = useState(null);

    useEffect(() => {

        api.get('/api/apps')
            .then(d => setApps(d?.apps || []))
            .catch(e => setAppsError(e?.err || e?.message || String(e)))
            .finally(() => setAppsLoading(false));

    }, [api]);

    // Flat list of {app, env} pairs for fast lookup against initialValue.
    const allEnvs = useMemo(
        () => apps.flatMap(a => (a.environments || []).map(e => ({ app: a, env: e }))),
        [apps]
    );

    const initialPreset = useMemo(() => {

        if (!initialValue) return '';
        const match = allEnvs.find(({ env }) => env.url === initialValue);
        return match ? match.env.url : CUSTOM;

    }, [initialValue, allEnvs]);

    const [selection, setSelection] = useState('');
    const [customUrl, setCustomUrl] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {

        if (!appsLoading) {

            setSelection(initialPreset);
            if (initialPreset === CUSTOM) setCustomUrl(initialValue || '');

        }

    }, [appsLoading, initialPreset, initialValue]);

    const isCustom = selection === CUSTOM;

    const submit = e => {

        e.preventDefault();

        if (!selection) {

            setError('Please select an environment.');
            return;

        }

        if (isCustom) {

            const trimmed = customUrl.trim();
            try {

                new URL(trimmed);

            } catch {

                setError('Must be a valid URL'); return;

            }

            onNext(trimmed);
            return;

        }

        onNext(selection);

    };

    return (

        <Card>
            <Card.Body>

                <Card.Title>
                    Target URL
                </Card.Title>
                <Card.Subtitle className="text-muted mb-3">
                    Pick the environment you want to test against.
                </Card.Subtitle>

                <Form onSubmit={submit}>

                    <Form.Group className="mb-3" controlId="step-url-env">
                        <Form.Label>
                            Environment
                        </Form.Label>
                        {appsLoading ? (
                            <div className="d-flex align-items-center gap-2 text-muted">
                                <Spinner animation="border" size="sm" />
                                <span>
                                    Loading environments…
                                </span>
                            </div>
                        ) : (
                            <Form.Select
                                autoFocus
                                required
                                onChange={e => {

                                    setSelection(e.target.value);
                                    setError(null);

                                }}
                                isInvalid={!!error && !isCustom}
                                value={selection}
                            >
                                <option value="">
                                    Select an environment…
                                </option>
                                {apps.map(app => (
                                    (app.environments || []).length === 0 ? null : (
                                        <optgroup key={app.id} label={app.name}>
                                            {app.environments.map(env => (
                                                <option key={env.id} value={env.url}>
                                                    {env.name}
                                                </option>
                                            ))}
                                        </optgroup>
                                    )
                                ))}
                                <option disabled>
                                    ──────────
                                </option>
                                <option value={CUSTOM}>
                                    Custom URL…
                                </option>
                            </Form.Select>
                        )}
                        {appsError && (
                            <div className="text-danger small mt-1">
                                Could not load environments:
                                {' '}
                                {appsError}
                            </div>
                        )}
                    </Form.Group>

                    {isCustom && (
                        <Form.Group className="mb-3" controlId="step-url-custom">
                            <Form.Label>
                                Custom URL
                            </Form.Label>
                            <Form.Control
                                required
                                onChange={e => {

                                    setCustomUrl(e.target.value);
                                    setError(null);

                                }}
                                isInvalid={!!error}
                                placeholder="https://app.example.com/login"
                                type="url"
                                value={customUrl}
                            />
                            <Form.Control.Feedback type="invalid">
                                {error}
                            </Form.Control.Feedback>
                        </Form.Group>
                    )}

                    {error && !isCustom && (
                        <div className="text-danger small mb-3">
                            {error}
                        </div>
                    )}

                    <div className="d-flex justify-content-end">
                        <Button disabled={appsLoading} type="submit" variant="primary">
                            Next
                        </Button>
                    </div>

                </Form>

            </Card.Body>
        </Card>

    );

};
